import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, ShoppingBag, ArrowRight, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('cop31_token')}` } });

export default function PaymentSuccessPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const [searchParams] = useSearchParams();
  const { slug } = useParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus]     = useState('polling'); // polling | success | failed | expired
  const [orderNumber, setOrderNumber] = useState('');
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 20; // 20 × 3s = 60s max polling

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/customer/checkout-status/${sessionId}`, getAuth());
        const { payment_status, status: txStatus, order_number } = r.data;

        if (payment_status === 'paid' || txStatus === 'completed') {
          setOrderNumber(order_number || '');
          setStatus('success');
          return;
        }
        if (txStatus === 'expired' || payment_status === 'failed') {
          setStatus('expired');
          return;
        }
        // Still pending — retry
        setAttempts(a => {
          if (a + 1 >= MAX_ATTEMPTS) { setStatus('failed'); return a + 1; }
          timer = setTimeout(poll, 3000);
          return a + 1;
        });
      } catch {
        setAttempts(a => {
          if (a + 1 >= MAX_ATTEMPTS) { setStatus('failed'); return a + 1; }
          timer = setTimeout(poll, 3000);
          return a + 1;
        });
      }
    };
    timer = setTimeout(poll, 2000); // first check after 2s
    return () => clearTimeout(timer);
  }, [sessionId]);

  const goToDashboard = () => navigate(`/musteri-admin/${slug}/dashboard`);
  const goToOrders    = () => navigate(`/musteri-admin/${slug}/orders`);
  const goToProducts  = () => navigate(`/musteri-admin/${slug}/products`);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* POLLING */}
        {status === 'polling' && (
          <div className="text-center space-y-5">
            <div className="h-20 w-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
              <Loader2 className="h-10 w-10 text-gray-400 animate-spin" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tr ? 'Ödeme İşleniyor...' : 'Processing Payment...'}</h1>
              <p className="text-gray-500 mt-2 text-sm">{tr ? 'Ödemeniz doğrulanıyor, lütfen bekleyin.' : 'Verifying your payment, please wait.'}</p>
            </div>
            <div className="flex justify-center gap-1">
              {[0,1,2].map(i => (
                <div key={i} className="h-2 w-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}

        {/* SUCCESS */}
        {status === 'success' && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center space-y-5">
            <div className="h-20 w-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-emerald-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tr ? 'Ödeme Başarılı!' : 'Payment Successful!'}</h1>
              {orderNumber && (
                <p className="text-emerald-600 font-mono font-semibold mt-1">
                  {tr ? `Sipariş No: ${orderNumber}` : `Order No: ${orderNumber}`}
                </p>
              )}
              <p className="text-gray-500 text-sm mt-2">
                {tr
                  ? 'Siparişiniz alındı. Tedarikçi kısa süre içinde sizinle iletişime geçecek.'
                  : 'Your order has been received. The supplier will contact you shortly.'}
              </p>
            </div>
            <div className="space-y-2 pt-2">
              <Button data-testid="view-orders-btn" onClick={goToOrders} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-full py-5 gap-2">
                <ShoppingBag className="h-4 w-4" />
                {tr ? 'Siparişlerimi Görüntüle' : 'View My Orders'}
              </Button>
              <Button onClick={goToDashboard} variant="outline" className="w-full rounded-full py-5 gap-2">
                {tr ? 'Panele Dön' : 'Back to Dashboard'}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* FAILED / EXPIRED */}
        {(status === 'failed' || status === 'expired') && (
          <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 text-center space-y-5">
            <div className="h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {status === 'expired'
                  ? (tr ? 'Oturum Süresi Doldu' : 'Session Expired')
                  : (tr ? 'Ödeme Başarısız' : 'Payment Failed')}
              </h1>
              <p className="text-gray-500 text-sm mt-2">
                {status === 'expired'
                  ? (tr ? 'Ödeme seansı sona erdi. Sepetinizden tekrar deneyin.' : 'Payment session expired. Please try again from your cart.')
                  : (tr ? 'Ödeme işlemi tamamlanamadı. Lütfen tekrar deneyin.' : 'Payment could not be completed. Please try again.')}
              </p>
            </div>
            <div className="space-y-2 pt-2">
              <Button onClick={goToProducts} className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-full py-5 gap-2">
                <RotateCcw className="h-4 w-4" />
                {tr ? 'Ürünlere Dön' : 'Back to Products'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
